/**
 * A deathmatch room's clock.
 *
 * A deathmatch room plays **rounds**: it gathers humans in a lobby, spawns everybody at once,
 * runs until there is a winner, congratulates them, and queues the next one. What is here is the
 * two waits either side of a round and the one enrolment rule under them — nothing else about the
 * mode is decided in this file, and **nothing in this build reads any of it yet**: a room's mode
 * is a declaration (`RoomSettings.mode`, defaulted to `classic`, so every room this build can
 * open is a classic one), and the match loop that spends these numbers is its own card.
 *
 * Its own file rather than a block in `simulation.ts`, on `votes.ts`' and `stars.ts`' precedent:
 * what is here is a *mode's* pacing rather than a value the growth curve or the wire was tuned
 * against. Everything it needs from the simulation is imported, so there is still one definition
 * of a tick and the seconds below cannot drift away from what a tick is worth.
 *
 * Each wait is stated in **seconds** and derived into **ticks**, in that order, because the
 * seconds are the decision and the ticks are the unit the simulation counts a deadline in — the
 * arrangement `SPAWN_PROTECTION_SECONDS` and `VOTE_DURATION_SECONDS` are already under.
 */

import { TICK_RATE_HZ } from './simulation.js';

/**
 * How long a deathmatch room waits in its lobby before starting a round.
 *
 * Thirty seconds, and it is a **ceiling on the wait rather than a countdown anybody has to
 * beat**: the round starts when the room is full of humans *or* when this runs out, whichever
 * comes first, so a busy room never waits and a quiet one never waits for ever. It is the number
 * the feature was asked for in exactly those words.
 *
 * Long enough that somebody who pressed Play the moment the last round ended is still there when
 * the next one starts, and short enough that a player who lands in an empty lobby is playing
 * within half a minute rather than deciding the room is broken and leaving — which is the failure
 * that matters, because a deathmatch room with nobody in it has no bots-topping-it-up loop to
 * make it look alive.
 */
export const DEATHMATCH_LOBBY_SECONDS = 30;

/** The same wait in ticks, which is what a lobby's deadline is measured in. */
export const DEATHMATCH_LOBBY_TICKS = DEATHMATCH_LOBBY_SECONDS * TICK_RATE_HZ;

/**
 * **THE WHOLE GAP BETWEEN TWO ROUNDS**, and one deadline on the wire covers all of it.
 *
 * It used to be a fifteen-second congratulation followed by a separate thirty-second lobby — 45 s
 * of nothing between rounds, and two clocks a player had to add up to answer "when do I play
 * again". This is that pair collapsed into one: the round ends, this window opens, and the next
 * round begins when it closes. The client splits it — `DEATHMATCH_CELEBRATION_SECONDS` of winner,
 * then the rest as a countdown — off a shared constant rather than off a second field.
 *
 * Thirty seconds because that is the player-facing number this mode already had: a lobby waits
 * up to `DEATHMATCH_LOBBY_SECONDS` for people to gather, and the gap after a round is the same
 * wait doing the same job with a celebration drawn over the first half of it.
 */
export const DEATHMATCH_ENDED_SECONDS = 30;

/** The same window in ticks, which is what a finished round's deadline is measured in. */
export const DEATHMATCH_ENDED_TICKS = DEATHMATCH_ENDED_SECONDS * TICK_RATE_HZ;

/**
 * The fewest humans a round may start with.
 *
 * One, and it is a rule about **humans** rather than about participants for the reason the reaper
 * exists: a room of nothing but bots playing rounds against each other for ever is precisely the
 * idle room `idleReapMs` is supposed to close, and it would keep a tick loop, a fill policy and a
 * host seat busy for an audience of nobody.
 *
 * One rather than two, because a single player waiting for a second one is a player who leaves —
 * a room whose bots fill it out is a room one human can play a round in, which is the same
 * bargain `BOT_DEFAULTS.minPlayers` already strikes for a classic room.
 */
export const DEATHMATCH_MIN_HUMANS = 1;

/**
 * The default ceiling on a deathmatch ROUND, in seconds.
 *
 * A last-one-standing round has no clock of its own: it ends when one body is left, which in a
 * fifty-player room on a 6 000-unit map was measured at over ten minutes — long enough that the
 * room stops being a queue anybody wants to wait in. Five minutes is short enough that a player
 * who joins the lobby knows roughly what they are committing to and long enough that a round is
 * still won by outplaying people rather than by the whistle.
 *
 * **It is a DEFAULT and not a rule**: `RoomSettings.roundSeconds` is per room and 0 turns it off,
 * which is the behaviour every deathmatch room had before this setting existed.
 */
export const DEATHMATCH_ROUND_SECONDS = 300;

/** The ceiling an operator may set, in seconds. An hour is past any round anybody wants. */
export const MAX_DEATHMATCH_ROUND_SECONDS = 3_600;

/** No limit, spelled the way `MatchState.phaseEndsAtTick` already spells "no deadline". */
export const DEATHMATCH_ROUND_UNLIMITED = 0;

/** A round limit in ticks, or 0 for none. Derived so the two can never disagree. */
export function deathmatchRoundTicks(seconds: number): number {
  return Math.max(0, Math.floor(seconds * TICK_RATE_HZ));
}

/**
 * How long the WINNER is celebrated for, of the intermission above.
 *
 * The intermission is one phase and one deadline on the wire; this is where the client stops
 * drawing confetti and starts drawing the countdown to the next round, and it is shared rather
 * than a number in the HUD because it is the shape of the ceremony rather than a style choice —
 * `DEATHMATCH_ENDED_SECONDS` minus this is what the big centred clock counts, and the two have to
 * add up.
 */
export const DEATHMATCH_CELEBRATION_SECONDS = 15;

/** The last seconds of a round, drawn across the middle of the screen. */
export const DEATHMATCH_ROUND_WARNING_SECONDS = 10;

/**
 * READY / SET / GO, one per second, over the last three of any countdown to a round starting.
 *
 * The words are here rather than in the HUD for `ROOM_MODE_LABELS`' reason: they are the mode's
 * vocabulary, and the count of them IS the length of the sequence — a fourth word is one more
 * second of it and nothing else to change.
 */
export const DEATHMATCH_GO_WORDS = ['READY', 'SET', 'GO!!!'] as const;

/**
 * What the room says about the player who just won.
 *
 * A curated pool rather than a table: these are words, they are identical in every deployment,
 * and they are read by a client that must be able to draw one without asking anybody — which is
 * `DEFAULT_SAYINGS`' and `BOT_NAME_POOL`'s arrangement exactly. A database would be a query per
 * round for a string that never changes.
 *
 * `%s` is the winner's name, substituted by `deathmatchTaunt`. Every line has to work for a BOT
 * winner too, because a bot can win a round — so none of them congratulates a person for being a
 * person, and none of them assumes the reader lost.
 */
export const DEATHMATCH_TAUNTS = [
  '%s ate the entire room and asked for seconds.',
  'Everybody else is a snack. %s is the meal.',
  '%s wins. The rest of you are pellets with opinions.',
  'Nobody could catch %s. Several of you tried. Loudly.',
  '%s stood still and let the room feed itself in.',
  'That was not close. %s made sure of it.',
  'The dome belongs to %s. Everyone else pays rent.',
  '%s went through this lobby like it was a buffet line.',
  'Round over. %s is bigger than your ambitions.',
  '%s survived. You did not. Statistically, that is on you.',
  'A moment of silence for everyone %s digested.',
  '%s is the last one standing and the only one worth watching.',
  'You were all warm-up. %s was the game.',
  '%s did that without breaking a sweat, or a cell.',
  'The arena has a new landlord and it is %s.',
] as const;

/**
 * FNV-1a over the round and the winner's name, so **every client in the room draws the same
 * line** without it costing a wire field or a draw on either RNG stream.
 *
 * `roomNameFor`'s trick, and for its reason: a value everybody has to agree about, derived from
 * something everybody already has. Picking it server-side would mean a string on the match frame
 * and a `PROTOCOL_VERSION` bump for a joke; picking it per client would show two people watching
 * the same round two different sentences.
 *
 * A round with no winner gets null — there is nobody to praise, and "nobody left standing" is
 * already the headline.
 */
export function deathmatchTaunt(round: number, winnerName: string | null): string | null {
  if (winnerName === null) return null;
  let hash = 0x811c9dc5;
  for (const char of `${round}:${winnerName}`) {
    hash ^= char.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  const line = DEATHMATCH_TAUNTS[hash % DEATHMATCH_TAUNTS.length];
  return line.replaceAll('%s', winnerName);
}

/**
 * How many finishers a round's scoreboard names.
 *
 * Five, because it is drawn under the winner's name on a card that is already carrying a
 * congratulation, a taunt and a clock — and because the interesting part of a finishing order is
 * the top of it. It bounds the list the room keeps as the round runs as well as the one that goes
 * on the wire: an earlier elimination can never outrank a later one, so the few most recent are
 * the only ones that can reach the board.
 */
/**
 * How many past winners the room's board shows.
 *
 * Five is Andy's own number and it fits the wait it fills. **Shared rather than the API's alone**,
 * because the client prepends the round that has just finished — the query cannot answer for it
 * until the worker has drained and posted it — and the two have to agree about how long the list
 * is or the board grows by a line during every ceremony. See `withCurrentRound`.
 */
export const RECENT_WINNERS_LIMIT = 5;

export const DEATHMATCH_STANDINGS = 5;
